import React, { createContext, useContext, useEffect, useId, useRef, useState } from 'react'
import { useInRouterContext, useLocation } from 'react-router-dom'
import Menu, { MenuProps, clamp, menuBottomGap } from './index'
import { MenuClose, useMenuClose } from './context'
import { MenuHeader } from './items'

// A menu the author can move out of the way and keep open while they work on what's behind it: the
// table's view panels (Filter, Sort, Group, Colour, Row height, Hide, Column order, Freeze, Lock)
// and Edit column. It opens where a Menu opens and, until it's moved, is a Menu in every way: a
// click outside closes it. It always has a header, drawn here from `title`: the menus' caption band
// (MenuHeader), which drags it with a mouse or a finger and shows a grip in its gutter on hover.
// Once it's been moved:
// - a click outside doesn't close it, so the author can click about the table;
// - the grip stays shown, and a × comes in at the end of the header to close it (as do Esc and its
//   own Save or Cancel);
// - going to another page or view (see FloatingMenuScope) closes it, which a click outside used to do.
// Only one is open at a time: opening another closes it, moved or not, without asking (see
// CloseOnLeaving). Keeping several open is left for when it's asked for.
// Every other menu stays a plain Menu: no header unless it draws one, and a click outside closes it.

type DragHandlers = Required<
  Pick<
    React.DOMAttributes<HTMLDivElement>,
    'onPointerDown' | 'onPointerMove' | 'onPointerUp' | 'onPointerCancel' | 'onLostPointerCapture'
  >
>

// Pressing one of the header's own controls uses that control; it doesn't start a drag.
const CONTROLS = 'button, a, input, textarea, select, [role="button"], [contenteditable="true"]'

const OPENED_EVENT = 'floating-menu-opened'

// How far the author has dragged the menu from where it opened, and whether they have, tied to that
// opening (see `anchor`).
interface Offset {
  anchor: string | null
  x: number
  y: number
  moved: boolean
}

interface DragStart {
  pointerX: number
  pointerY: number
  x: number
  y: number
  rect: DOMRect
}

// What a floating menu belongs to (a table's view, say), set by its owner. A table embedded in a
// document switches views without leaving the page, so the address alone can't tell.
const ScopeContext = createContext<string | null>(null)

/** Wraps what a floating menu belongs to: when `value` changes, any floating menu inside closes. */
export const FloatingMenuScope: React.FC<{ value: string; children: React.ReactNode }> = ({ value, children }) => {
  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>
}

// A moved menu takes no click outside, so leaving where it was opened (another page, or another view
// of an embedded table) has to close it; the table it belongs to stays mounted across tables and
// views, and would otherwise keep it open over the next one. By the time it knows, the author has
// already left, so it closes without asking about unsaved changes (kept open, it would be editing
// what's on the new page with the old draft gone, so a Cancel couldn't keep anything), and without
// its onClose: Edit column's saves what's still pending there, which would now land on the new view.
const useCloseOnLeaving = (place: string | null, close: MenuClose | null): void => {
  const openedIn = useRef(place)
  useEffect(() => {
    if (place === openedIn.current) return
    openedIn.current = place
    close?.({ force: true, discard: true })
  }, [place, close])
}

// Inside the Menu, for its close. Only where there's a router: tests render menus without one.
const CloseOnNavigation: React.FC = () => {
  useCloseOnLeaving(useLocation().pathname, useMenuClose())
  return null
}

interface HeaderProps {
  title: string
  action?: React.ReactNode
  drag: DragHandlers
  moved: boolean
}

// Inside the Menu, so it closes it when the page or view it belongs to changes, and when another
// floating menu opens: one at a time, so two never stand over the table at once. That close doesn't
// ask about unsaved changes either, so a Cancel can't leave two open. Rendered full screen too,
// where there's no header.
const CloseOnLeaving: React.FC<{ instance: string }> = ({ instance }) => {
  const close = useMenuClose()
  useCloseOnLeaving(useContext(ScopeContext), close)
  useEffect(() => {
    const onOpened = (event: Event) => {
      // Same page and view, so its onClose still saves what's pending (Edit column's rename).
      if ((event as CustomEvent<string>).detail !== instance) close?.({ force: true })
    }
    document.addEventListener(OPENED_EVENT, onOpened)
    return () => document.removeEventListener(OPENED_EVENT, onOpened)
  }, [instance, close])
  return useInRouterContext() ? <CloseOnNavigation /> : null
}

// Inside the Menu, for its ×.
const Header: React.FC<HeaderProps> = ({ title, action, drag, moved }) => {
  const close = useMenuClose()
  return (
    // The × asks first about unsaved changes, as Esc does.
    <MenuHeader action={action} onClose={close ? () => close() : undefined} drag={drag} moved={moved}>
      {title}
    </MenuHeader>
  )
}

export interface FloatingMenuProps extends Omit<MenuProps, 'closeOnClickOutside' | 'offset'> {
  /** The header's title. Every floating menu has a header: it's what the author drags it by. */
  title: string
  /** Quiet actions in the header, before its ×: a MenuHeaderAction ("Clear all"). */
  action?: React.ReactNode
}

const FloatingMenu: React.FC<FloatingMenuProps> = ({ title, action, children, ...props }) => {
  const { menuState, viewportMargin = 0, fullScreen = false } = props
  const instance = useId()

  // The offset belongs to one opening at one anchor: closing the menu, or opening it from another
  // trigger, puts it back next to that trigger. Swapping what the same opening shows (Edit column's
  // formula panel) leaves it where it was moved.
  const anchor = menuState.open ? `${menuState.top}|${menuState.left}|${menuState.bottom}` : null
  const [offset, setOffset] = useState<Offset>({ anchor, x: 0, y: 0, moved: false })
  if (offset.anchor !== anchor) setOffset({ anchor, x: 0, y: 0, moved: false })
  const current = offset.anchor === anchor ? offset : { x: 0, y: 0, moved: false }
  const { x, y, moved } = current
  const dragStartRef = useRef<DragStart | null>(null)

  // Opening it closes any other floating menu (see CloseOnLeaving).
  useEffect(() => {
    if (anchor) document.dispatchEvent(new CustomEvent(OPENED_EVENT, { detail: instance }))
  }, [anchor, instance])

  const endDrag = () => {
    dragStartRef.current = null
  }

  // Pointer events, so a finger moves the menu as well as a mouse. Capturing the pointer keeps the
  // drag on the header when it passes over the table, so letting go there doesn't select the cell
  // underneath.
  const drag: DragHandlers = {
    onPointerDown: (event) => {
      const target = event.target as Element
      const menu = event.currentTarget.closest('[data-menu]')
      if (event.button !== 0 || !menu || target.closest(CONTROLS)) return
      event.preventDefault() // no text selection while dragging
      event.currentTarget.setPointerCapture?.(event.pointerId)
      dragStartRef.current = {
        pointerX: event.clientX,
        pointerY: event.clientY,
        x,
        y,
        rect: menu.getBoundingClientRect()
      }
    },
    onPointerMove: (event) => {
      const start = dragStartRef.current
      if (!start) return
      // The whole menu stays on screen, so its header can always be reached to drag it back.
      const { rect } = start
      const dx = clamp(
        event.clientX - start.pointerX,
        viewportMargin - rect.left,
        window.innerWidth - viewportMargin - rect.right
      )
      const dy = clamp(
        event.clientY - start.pointerY,
        viewportMargin - rect.top,
        window.innerHeight - menuBottomGap(viewportMargin) - rect.bottom
      )
      // A press that doesn't move it (a click on the header) leaves it a plain menu.
      if (dx || dy || moved) setOffset({ anchor, x: start.x + dx, y: start.y + dy, moved: true })
    },
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
    onLostPointerCapture: endDrag
  }

  return (
    <Menu {...props} closeOnClickOutside={!moved} offset={{ x, y }}>
      <CloseOnLeaving instance={instance} />
      {/* Full screen, it covers the window, so there's nothing to drag it off and no header. */}
      {!fullScreen && <Header title={title} action={action} drag={drag} moved={moved} />}
      {children}
    </Menu>
  )
}

export default FloatingMenu
